import React from "react"

export interface AdvisorLogoIconProps extends React.SVGAttributes<SVGSVGElement> {
  size?: number | "sm" | "md" | "lg" | "xl"
}

const sizeMap = { sm: 24, md: 32, lg: 40, xl: 48 }

/**
 * ADVisor NOVA Mark — 6-spoke star inscribed in circle
 */
const AdvisorLogoIcon = React.forwardRef<SVGSVGElement, AdvisorLogoIconProps>(
  ({ size = "md", className, ...props }, ref) => {
    const px = typeof size === "number" ? size : sizeMap[size]
    return (
      <svg
        ref={ref}
        width={px}
        height={px}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        {...props}
      >
        {/* Outer circle */}
        <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.9" />
        <circle cx="24" cy="24" r="17" fill="currentColor" opacity="0.06" />
        {/* 6 spokes */}
        {[0, 30, 60, 90, 120, 150].map((angle) => (
          <line
            key={angle}
            x1="2"
            y1="24"
            x2="46"
            y2="24"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.88"
            transform={angle ? `rotate(${angle},24,24)` : undefined}
          />
        ))}
        {/* 12 tip dots */}
        {[0, 30, 60, 90, 120, 150].map((angle) => (
          <g key={`dots-${angle}`} transform={angle ? `rotate(${angle},24,24)` : undefined}>
            <circle cx="46" cy="24" r="2" fill="currentColor" opacity="0.85" />
            <circle cx="2" cy="24" r="2" fill="currentColor" opacity="0.85" />
          </g>
        ))}
        {/* Center hole */}
        <circle cx="24" cy="24" r="4" fill="var(--advisor-logo-bg, #2A4A7E)" stroke="currentColor" strokeWidth="1" />
      </svg>
    )
  }
)
AdvisorLogoIcon.displayName = "AdvisorLogoIcon"

export interface AdvisorLogoTextProps extends React.SVGAttributes<SVGSVGElement> {
  size?: number | "sm" | "md" | "lg" | "xl"
}

/**
 * ADVisor Wordmark
 */
const AdvisorLogoText = React.forwardRef<SVGSVGElement, AdvisorLogoTextProps>(
  ({ size = "md", className, ...props }, ref) => {
    const h = typeof size === "number" ? size : sizeMap[size]
    const w = h * 3.5
    return (
      <svg
        ref={ref}
        width={w}
        height={h}
        viewBox="0 0 140 40"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        {...props}
      >
        <text
          x="0"
          y="32"
          fontFamily="Georgia, serif"
          fontWeight="900"
          fontSize="34"
          letterSpacing="-1"
          fill="currentColor"
        >
          ADVisor
        </text>
      </svg>
    )
  }
)
AdvisorLogoText.displayName = "AdvisorLogoText"

export interface AdvisorLogoProps extends React.HTMLAttributes<HTMLDivElement> {
  iconSize?: number | "sm" | "md" | "lg" | "xl"
  variant?: "light" | "dark"
  showText?: boolean
}

/**
 * ADVisor Logo — Combined icon and text
 */
const AdvisorLogo = React.forwardRef<HTMLDivElement, AdvisorLogoProps>(
  ({ iconSize = "md", variant = "light", showText = true, className, ...props }, ref) => {
    const color = variant === "dark" ? "#FFFFFF" : "#2A4A7E"
    return (
      <div
        ref={ref}
        className={className}
        style={{ display: "flex", alignItems: "center", gap: "8px", color }}
        {...props}
      >
        <AdvisorLogoIcon size={iconSize} />
        {showText && <AdvisorLogoText size={iconSize} />}
      </div>
    )
  }
)
AdvisorLogo.displayName = "AdvisorLogo"

export { AdvisorLogoIcon, AdvisorLogoText, AdvisorLogo }
